import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { TerminalsStore } from '../../features/terminals/core/stores/TerminalsStore';
import { ApiErrorResponse } from '../classes/ApiErrorResponse';

/**
 * Centralized logging to Sentry for handled errors that would otherwise only
 * show a toast and never reach monitoring. Enriches every event with the
 * current terminal/session context so a failed operation can be traced back to
 * a concrete terminal, mandator, session and basket.
 *
 * Sentry is only initialized outside localhost (see main.ts), so these calls
 * are safe no-ops during local development.
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService {

  private readonly _terminalsStore = inject(TerminalsStore);

  /**
   * Capture a handled API error. `operation` is a short stable label used as a
   * Sentry tag (e.g. 'checkout', 'print-transaction'). `extra` carries any
   * operation-specific payload (e.g. a cart/transaction snapshot).
   */
  public captureApiError(
    operation: string,
    error: ApiErrorResponse,
    extra: Record<string, unknown> = {}
  ): void {
    try {
      const errorCode = error?.error?.message ?? 'Unknown';

      Sentry.withScope(scope => {
        scope.setLevel('error');
        scope.setTag('operation', operation);
        scope.setTag('errorCode', errorCode);
        scope.setTag('httpStatus', String(error?.status ?? ''));
        scope.setContext('terminalContext', this.buildTerminalContext());
        scope.setContext('apiError', {
          operation,
          errorCode,
          status: error?.status,
          statusText: error?.statusText,
          url: error?.url,
          body: error?.error,
          ...extra
        });

        Sentry.captureMessage(`${operation} failed: ${errorCode}`, 'error');
      });
    } catch { /* logging must never interrupt the caller */ }
  }

  public captureIssue(
    operation: string,
    message: string,
    extra: Record<string, unknown> = {}
  ): void {
    try {
      Sentry.withScope(scope => {
        scope.setLevel('error');
        scope.setTag('operation', operation);
        scope.setContext('terminalContext', this.buildTerminalContext());
        scope.setContext('issue', extra);
        Sentry.captureMessage(message, 'error');
      });
    } catch { /* logging must never interrupt the caller */ }
  }

  private buildTerminalContext(): Record<string, unknown> {
    const terminal = this._terminalsStore.selectedTerminal();

    if (!terminal) {
      return { terminal: null };
    }

    return {
      terminalId: terminal.id,
      cinemaId: terminal.cinemaId,
      mandatorId: terminal.mandator?.id,
      sessionId: terminal.currentSession?.id,
      basketId: terminal.currentSession?.currentBasketId,
      workerNumber: terminal.worker?.number
    };
  }
}
